import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  FileBarChart, UserCheck, Car, RefreshCw, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { exportToCsv, printReport, ExportPrintBar } from '../../utils/reportExport';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

function fmt(n) { return typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'; }
function fmtKm(n) { return typeof n === 'number' ? `${n.toLocaleString('en-IN')} km` : '-'; }

function StatusBadge({ status }) {
  const map = {
    ACTIVE: 'bg-blue-500',
    COMPLETED: 'bg-green-500',
    CANCELLED: 'bg-red-500',
    IN_TRANSIT: 'bg-purple-500',
    LOADING: 'bg-yellow-500',
    UNLOADING: 'bg-orange-500',
    PARKING: 'bg-gray-500',
  };
  return <Badge className={`${map[status] || 'bg-gray-400'} text-white text-xs`}>{status}</Badge>;
}

function BalanceBadge({ net }) {
  if (net > 0) return <span className="text-green-600 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" />{fmt(net)}</span>;
  if (net < 0) return <span className="text-red-600 font-semibold flex items-center gap-1"><TrendingDown className="w-3 h-3" />{fmt(net)}</span>;
  return <span className="text-gray-500 flex items-center gap-1"><Minus className="w-3 h-3" />{fmt(net)}</span>;
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN');
}

// ─── Date/Filter Bar (shared) ──────────────────────────────────
function FilterBar({ dateFrom, setDateFrom, dateTo, setDateTo, status, setStatus, onApply, showStatus = true, children }) {
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Date From</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="font-ibm h-9 w-40" />
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Date To</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="font-ibm h-9 w-40" />
        </div>
        {showStatus && (
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Status</label>
            <Select value={status || '__all__'} onValueChange={v => setStatus(v === '__all__' ? '' : v)}>
              <SelectTrigger className="font-ibm h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Statuses</SelectItem>
                {['ACTIVE', 'COMPLETED', 'CANCELLED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button size="sm" onClick={onApply} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
          <RefreshCw className="w-3 h-3 mr-1" />Apply
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); if (setStatus) setStatus(''); onApply(); }} className="h-9">
          Clear
        </Button>
        {children}
      </div>
    </Card>
  );
}

// ─── Tab 1: Trip Summary & P&L ─────────────────────────────────
function TripSummary() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    if (status) p.append('status', status);
    fetch(`${API}/api/reports/fleet/summary?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setTrips(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateFrom, dateTo, status]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const totalFreight = trips.reduce((s, t) => s + (t.freight || 0), 0);
  const totalExpenses = trips.reduce((s, t) => s + (t.total_expenses || 0), 0);
  const totalKm = trips.reduce((s, t) => s + (t.total_km || 0), 0);
  const netBalance = trips.reduce((s, t) => s + (t.net_balance || 0), 0);

  const TRIP_COLS = [
    { header: 'Trip #', key: 'trip_number' },
    { header: 'Date', key: 'trip_date', format: v => v ? new Date(v).toLocaleDateString('en-IN') : '-' },
    { header: 'Status', key: 'status' },
    { header: 'Type', key: 'trip_type' },
    { header: 'From', key: 'from_location' },
    { header: 'To', key: 'to_location' },
    { header: 'Vehicle', key: 'vehicle_registration' },
    { header: 'Driver', key: 'driver_name' },
    { header: 'Freight (₹)', key: 'freight', format: v => (v || 0).toFixed(2) },
    { header: 'Expenses (₹)', key: 'total_expenses', format: v => (v || 0).toFixed(2) },
    { header: 'Net Balance (₹)', key: 'net_balance', format: v => (v || 0).toFixed(2) },
    { header: 'KM', key: 'total_km', format: v => v || 0 },
  ];
  const handleExport = () => exportToCsv('Fleet_Trip_Summary', TRIP_COLS, trips);
  const handlePrint = () => printReport('Fleet Trip Summary & P&L', TRIP_COLS, [
    ...trips,
    { trip_number: 'TOTALS', freight: totalFreight, total_expenses: totalExpenses, net_balance: netBalance, total_km: totalKm },
  ]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Trips', value: trips.length, color: 'text-blue-600' },
          { label: 'Total Freight', value: fmt(totalFreight), color: 'text-green-600', small: true },
          { label: 'Total Expenses', value: fmt(totalExpenses), color: 'text-orange-600', small: true },
          { label: 'Total KM', value: fmtKm(totalKm), color: 'text-purple-600', small: true },
        ].map(s => (
          <Card key={s.label} className="p-4">
            <p className={`${s.small ? 'text-base' : 'text-2xl'} font-bold font-manrope ${s.color}`}>{s.value}</p>
            <p className="text-xs text-text-secondary font-ibm mt-1">{s.label}</p>
          </Card>
        ))}
      </div>

      <FilterBar dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} status={status} setStatus={setStatus} onApply={fetch_}>
        <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || trips.length === 0} />
      </FilterBar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Trip #', 'Date', 'Status', 'Type', 'Route', 'Vehicle', 'Driver', 'Freight', 'Expenses', 'Net Balance', 'KM'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : trips.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-text-secondary">No trips found for the selected filters</td></tr>
              ) : trips.map(t => (
                <tr key={t.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-semibold text-text-dark font-ibm">{t.trip_number}</td>
                  <td className="px-4 py-3 text-text-dark whitespace-nowrap">{formatDate(t.trip_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3 text-text-secondary text-xs capitalize">{t.trip_type} / {t.trip_kind?.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-text-dark text-xs">{t.from_location || '-'} → {t.to_location || '-'}</td>
                  <td className="px-4 py-3 font-ibm">{t.vehicle_registration || '-'}</td>
                  <td className="px-4 py-3">{t.driver_name || '-'}</td>
                  <td className="px-4 py-3 font-ibm text-green-700 font-medium">{fmt(t.freight)}</td>
                  <td className="px-4 py-3 font-ibm text-orange-700">{fmt(t.total_expenses)}</td>
                  <td className="px-4 py-3"><BalanceBadge net={t.net_balance} /></td>
                  <td className="px-4 py-3 font-ibm text-text-secondary">{t.total_km > 0 ? fmtKm(t.total_km) : '-'}</td>
                </tr>
              ))}
            </tbody>
            {trips.length > 0 && (
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td colSpan={7} className="px-4 py-3 font-semibold text-text-dark">Totals ({trips.length} trips)</td>
                  <td className="px-4 py-3 font-bold text-green-700 font-ibm">{fmt(totalFreight)}</td>
                  <td className="px-4 py-3 font-bold text-orange-700 font-ibm">{fmt(totalExpenses)}</td>
                  <td className="px-4 py-3"><BalanceBadge net={netBalance} /></td>
                  <td className="px-4 py-3 font-ibm font-bold text-purple-700">{fmtKm(totalKm)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Tab 2: Driver Performance ─────────────────────────────────
function DriverPerformance() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    fetch(`${API}/api/reports/fleet/driver-performance?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setDrivers(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const DRIVER_COLS = [
    { header: 'Driver', key: 'driver_name' },
    { header: 'DL No.', key: 'dl_no' },
    { header: 'Vendor', key: 'vendor_name' },
    { header: 'Total Trips', key: 'trip_count' },
    { header: 'Completed', key: 'completed_trips' },
    { header: 'Expenses (₹)', key: 'total_expenses', format: v => (v || 0).toFixed(2) },
    { header: 'Receipts (₹)', key: 'total_receipts', format: v => (v || 0).toFixed(2) },
    { header: 'Net Balance (₹)', key: 'net_balance', format: v => (v || 0).toFixed(2) },
    { header: 'Total KM', key: 'total_km', format: v => v || 0 },
  ];
  const handleDriverExport = () => exportToCsv('Fleet_Driver_Performance', DRIVER_COLS, drivers);
  const handleDriverPrint = () => printReport('Fleet Driver Performance', DRIVER_COLS, drivers);

  return (
    <div className="space-y-5">
      <FilterBar dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} onApply={fetch_} showStatus={false}>
        <ExportPrintBar onExport={handleDriverExport} onPrint={handleDriverPrint} disabled={loading || drivers.length === 0} />
      </FilterBar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Driver', 'DL No.', 'Vendor', 'Total Trips', 'Completed', 'Total Expenses', 'Total Receipts', 'Net Balance', 'Total KM'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : drivers.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">No driver data found</td></tr>
              ) : drivers.map((d, idx) => (
                <tr key={d.driver_id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {idx === 0 && <span className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold text-yellow-900">1</span>}
                      <span className="font-semibold text-text-dark">{d.driver_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-ibm text-text-secondary">{d.dl_no || '-'}</td>
                  <td className="px-4 py-3 text-text-secondary">{d.vendor_name || '-'}</td>
                  <td className="px-4 py-3 font-bold font-ibm text-blue-700">{d.trip_count}</td>
                  <td className="px-4 py-3 font-ibm text-green-700">{d.completed_trips}</td>
                  <td className="px-4 py-3 font-ibm text-orange-700">{fmt(d.total_expenses)}</td>
                  <td className="px-4 py-3 font-ibm text-green-700">{fmt(d.total_receipts)}</td>
                  <td className="px-4 py-3"><BalanceBadge net={d.net_balance} /></td>
                  <td className="px-4 py-3 font-ibm text-text-secondary">{d.total_km > 0 ? fmtKm(d.total_km) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Tab 3: Vehicle Performance ────────────────────────────────
function VehiclePerformance() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    fetch(`${API}/api/reports/fleet/vehicle-performance?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setVehicles(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const VEH_COLS = [
    { header: 'Reg. No.', key: 'registration_number' },
    { header: 'Vehicle Name', key: 'vehicle_name' },
    { header: 'Type', key: 'vehicle_type' },
    { header: 'Vendor', key: 'vendor_name' },
    { header: 'Total Trips', key: 'trip_count' },
    { header: 'Completed', key: 'completed_trips' },
    { header: 'Total KM', key: 'total_km', format: v => v || 0 },
    { header: 'Freight (₹)', key: 'total_freight', format: v => (v || 0).toFixed(2) },
    { header: 'Expenses (₹)', key: 'total_expenses', format: v => (v || 0).toFixed(2) },
    { header: 'Net Balance (₹)', key: 'net_balance', format: v => (v || 0).toFixed(2) },
  ];
  const handleVehExport = () => exportToCsv('Fleet_Vehicle_Performance', VEH_COLS, vehicles);
  const handleVehPrint = () => printReport('Fleet Vehicle Performance', VEH_COLS, vehicles);

  return (
    <div className="space-y-5">
      <FilterBar dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} onApply={fetch_} showStatus={false}>
        <ExportPrintBar onExport={handleVehExport} onPrint={handleVehPrint} disabled={loading || vehicles.length === 0} />
      </FilterBar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Reg. No.', 'Vehicle Name', 'Type', 'Vendor', 'Total Trips', 'Completed', 'Total KM', 'Total Freight', 'Total Expenses', 'Net Balance'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : vehicles.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-text-secondary">No vehicle data found</td></tr>
              ) : vehicles.map((v, idx) => (
                <tr key={v.vehicle_id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {idx === 0 && <span className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold text-yellow-900">1</span>}
                      <span className="font-semibold text-text-dark font-ibm">{v.registration_number}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-text-dark">{v.vehicle_name || '-'}</td>
                  <td className="px-4 py-3 text-text-secondary">{v.vehicle_type || '-'}</td>
                  <td className="px-4 py-3 text-text-secondary">{v.vendor_name || '-'}</td>
                  <td className="px-4 py-3 font-bold font-ibm text-blue-700">{v.trip_count}</td>
                  <td className="px-4 py-3 font-ibm text-green-700">{v.completed_trips}</td>
                  <td className="px-4 py-3 font-ibm text-purple-700">{v.total_km > 0 ? fmtKm(v.total_km) : '-'}</td>
                  <td className="px-4 py-3 font-ibm text-green-700">{fmt(v.total_freight)}</td>
                  <td className="px-4 py-3 font-ibm text-orange-700">{fmt(v.total_expenses)}</td>
                  <td className="px-4 py-3"><BalanceBadge net={v.net_balance} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────
const TABS = [
  { id: 'summary', label: 'Trip Summary & P&L', icon: FileBarChart },
  { id: 'driver', label: 'Driver Performance', icon: UserCheck },
  { id: 'vehicle', label: 'Vehicle Performance', icon: Car },
];

export default function FleetReports() {
  const [activeTab, setActiveTab] = useState('summary');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-dark font-manrope">Fleet Reports</h1>
        <p className="text-sm text-text-secondary mt-1">Trip P&L, driver performance, and vehicle utilization analysis</p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`tab-fleet-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium font-ibm border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-accent-blue text-accent-blue'
                  : 'border-transparent text-text-secondary hover:text-text-dark'
              }`}
            >
              <Icon className="w-4 h-4" />{tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'summary' && <TripSummary />}
      {activeTab === 'driver' && <DriverPerformance />}
      {activeTab === 'vehicle' && <VehiclePerformance />}
    </div>
  );
}
